import { useMutation } from 'react-query';
import type { StripeSessionDto } from '@subscriptions/application/dtos';
import { getPaymentMethodUseCases } from '@subscriptions/composition';
import type { PaymentSessionMode } from '@subscriptions/domain/types';

/**
 * Variables accepted by the generate-session mutation.
 *
 * `mode` is derived by the caller from the block's license status and `isUpdate`
 * reflects whether the block already has an attached payment method.
 */
export type GenerateBlockPaymentSessionVariables = {
  blockUuid: string;
  mode: PaymentSessionMode;
  isUpdate: boolean;
};

/**
 * Hook for creating a Stripe checkout session for a subscription block (CARD path).
 *
 * Resolves with the {@link StripeSessionDto} (`clientSecret` / `providerSessionId` /
 * `mode`) that the checkout step mounts. The `clientSecret` / `providerSessionId`
 * are never rendered or logged here (charter §5).
 *
 * @returns React Query mutation result
 */
export const useGenerateBlockPaymentSession = () => {
  const { generateSession } = getPaymentMethodUseCases();

  return useMutation<StripeSessionDto, Error, GenerateBlockPaymentSessionVariables>(({ blockUuid, mode, isUpdate }) =>
    generateSession.execute(blockUuid, mode, isUpdate),
  );
};
